import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q5husnlsngrm4llrt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg =
        (parsed?.meta as Record<string, string>)?.message ||
        (parsed?.message as string) ||
        (parsed?.meta as Record<string, string>)?.detail ||
        responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查连接后重试');
    }
  };

  return (
    <section>
      <h2 className="text-xl font-heading text-foreground-900 mb-5">联系我们</h2>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1">
          <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                您的名字 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                placeholder="请输入您的名字"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                电子邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                placeholder="请输入您的邮箱"
              />
            </div>
            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                主题
              </label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                placeholder="请选择或输入主题"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={5}
                maxLength={500}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200 resize-none"
                placeholder="请输入您的留言（最多500字）"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1">最多500字</p>
            </div>

            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="website-url-field"
            />

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full bg-primary-500 text-background-50 py-2.5 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {status === 'loading' ? '发送中...' : '发送留言'}
            </button>
          </form>

          {status === 'success' && (
            <div className="mt-4 p-4 bg-accent-100 text-accent-700 text-sm rounded-lg">
              <i className="ri-check-line mr-1.5"></i>
              留言发送成功！我们会在24小时内回复您。
            </div>
          )}
          {status === 'error' && (
            <div className="mt-4 p-4 bg-primary-100 text-primary-700 text-sm rounded-lg">
              <i className="ri-error-warning-line mr-1.5"></i>
              {errorMsg || '发送失败，请稍后重试'}
            </div>
          )}
        </div>

        <div className="w-full lg:w-72 flex-shrink-0">
          <div className="bg-background-100/70 rounded-xl p-5 space-y-4">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                <i className="ri-mail-line text-accent-500"></i>
              </span>
              <div>
                <p className="text-xs font-medium text-foreground-800 mb-0.5">电子邮箱</p>
                <p className="text-xs text-foreground-500">contact@caimogu.example.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                <i className="ri-wechat-line text-accent-500"></i>
              </span>
              <div>
                <p className="text-xs font-medium text-foreground-800 mb-0.5">微信公众号</p>
                <p className="text-xs text-foreground-500">踩蘑菇攻略大全</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                <i className="ri-bilibili-line text-accent-500"></i>
              </span>
              <div>
                <p className="text-xs font-medium text-foreground-800 mb-0.5">B站账号</p>
                <p className="text-xs text-foreground-500">踩蘑菇攻略菌</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}